import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ShieldCheck, Loader2, Camera, ArrowRight, ArrowLeft, Check, AlertTriangle,
  User, Receipt, CreditCard, Sparkles, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { WageUpLockup } from "@/components/WageUpLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatSSN = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};
const formatPhone = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};
const formatDigits = (v, max) => (v || "").replace(/\D/g, "").slice(0, max);

export default function EmployeeOnboardPage() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    legal_first_name: "", legal_middle_name: "", legal_last_name: "", preferred_name: "",
    ssn: "", date_of_birth: "",
    email: "", phone: "",
    address_line1: "", address_line2: "", city: "", state: "IL", zip_code: "",
    federal_filing_status: "Single", federal_allowances: 0, federal_extra_withholding: 0,
    il_allowances: 0, il_extra_withholding: 0,
    bank_name: "", bank_routing: "", bank_account: "", bank_account_type: "checking",
    emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_relation: "",
    w4_signed_date: new Date().toISOString().slice(0, 10),
  });

  useEffect(() => {
    axios.get(`${API}/payroll/vault/invite/${token}`)
      .then(r => setInvite(r.data))
      .catch(err => setError(err.response?.data?.detail || "Link not valid"));
  }, [token]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Employee can't call extract-document (requires master code). Self-onboarding is manual entry only.
  // Master can later upload attachments separately.

  const is1099 = invite?.pay_category === "1099";
  const totalSteps = is1099 ? 2 : 3;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, employee_code: invite.employee_code };
      ["ssn", "date_of_birth", "bank_routing", "bank_account"].forEach(f => {
        if (!payload[f] || String(payload[f]).trim() === "") delete payload[f];
      });
      await axios.post(`${API}/payroll/vault/invite/${token}/submit`, payload);
      setDone(true);
    } catch (err) {
      toast.error("Submit failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-rose-600 mb-3" />
          <h1 className="text-xl font-bold text-stone-900 mb-2">Link unavailable</h1>
          <p className="text-sm text-stone-600">{error}</p>
          <p className="text-xs text-stone-400 mt-4">Please ask your manager to send you a new link.</p>
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-stone-500" />
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-emerald-700" />
          </div>
          <h1 className="text-2xl font-bold text-stone-900 mb-2">All set, {invite.employee_name.split(" ")[0]}!</h1>
          <p className="text-sm text-stone-600">Your info was received securely. Your manager will be in touch.</p>
          <p className="text-xs text-stone-400 mt-4">You can close this page now.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100">
      <div className="bg-gradient-to-r from-[#6B0F0F] via-[#A61B1B] to-[#C4231D] text-white px-4 py-3 shadow-md">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <WageUpLockup logoSize={32} tone="light" />
          <span className="text-xs opacity-90 font-semibold">Secure Onboarding</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <p className="text-sm text-stone-500">Hi {invite.employee_name.split(" ")[0]}! 👋</p>
          <h1 className="text-xl sm:text-2xl font-bold text-stone-900">Let&apos;s get you set up for payroll.</h1>
          <p className="text-sm text-stone-600 mt-1">This takes about 3 minutes. Your info is encrypted before it leaves your phone.</p>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            {[
              { id: 1, title: "About You", icon: User },
              ...(is1099 ? [] : [{ id: 2, title: "Tax Setup", icon: Receipt }]),
              { id: totalSteps, title: "Bank Info", icon: CreditCard },
            ].map((s, i, arr) => {
              const Icon = s.icon;
              const active = step === s.id;
              const completed = step > s.id;
              return (
                <React.Fragment key={s.id}>
                  <div className="flex flex-col items-center text-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                      completed ? "bg-emerald-100 text-emerald-700"
                        : active ? "bg-[#A61B1B] text-white shadow"
                        : "bg-stone-100 text-stone-400"
                    }`}>
                      {completed ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <span className={`text-xs font-semibold ${active ? "text-stone-900" : "text-stone-400"}`}>{s.title}</span>
                  </div>
                  {i < arr.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${step > s.id ? "bg-emerald-300" : "bg-stone-200"}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div className="bg-white rounded-lg shadow-sm p-5 space-y-5">
          {step === 1 && (
            <>
              <h2 className="text-lg font-bold">Tell us about you</h2>
              <Grid2>
                <Field label="Legal First Name *"><Input value={form.legal_first_name} onChange={e => update("legal_first_name", e.target.value)} data-testid="emp-fname" /></Field>
                <Field label="Legal Last Name *"><Input value={form.legal_last_name} onChange={e => update("legal_last_name", e.target.value)} data-testid="emp-lname" /></Field>
              </Grid2>
              <Grid2>
                <Field label="Social Security Number *">
                  <Input value={form.ssn} onChange={e => update("ssn", formatSSN(e.target.value))} placeholder="123-45-6789" autoComplete="off" data-testid="emp-ssn" />
                </Field>
                <Field label="Date of Birth *">
                  <Input type="date" value={form.date_of_birth} onChange={e => update("date_of_birth", e.target.value)} data-testid="emp-dob" />
                </Field>
              </Grid2>
              <Grid2>
                <Field label="Email"><Input type="email" value={form.email} onChange={e => update("email", e.target.value)} /></Field>
                <Field label="Phone"><Input value={form.phone} onChange={e => update("phone", formatPhone(e.target.value))} placeholder="(847) 555-0123" /></Field>
              </Grid2>
              <Field label="Street Address *"><Input value={form.address_line1} onChange={e => update("address_line1", e.target.value)} placeholder="123 Main St" /></Field>
              <Field label="Apt / Unit"><Input value={form.address_line2} onChange={e => update("address_line2", e.target.value)} placeholder="Optional" /></Field>
              <Grid3>
                <Field label="City *"><Input value={form.city} onChange={e => update("city", e.target.value)} /></Field>
                <Field label="State *"><Input value={form.state} onChange={e => update("state", e.target.value.toUpperCase().slice(0, 2))} /></Field>
                <Field label="ZIP *"><Input value={form.zip_code} onChange={e => update("zip_code", formatDigits(e.target.value, 5))} /></Field>
              </Grid3>
              <h3 className="text-sm font-bold mt-4 text-stone-700">Emergency Contact (optional)</h3>
              <Grid3>
                <Field label="Name"><Input value={form.emergency_contact_name} onChange={e => update("emergency_contact_name", e.target.value)} /></Field>
                <Field label="Phone"><Input value={form.emergency_contact_phone} onChange={e => update("emergency_contact_phone", formatPhone(e.target.value))} /></Field>
                <Field label="Relation"><Input value={form.emergency_contact_relation} onChange={e => update("emergency_contact_relation", e.target.value)} placeholder="Spouse, Parent…" /></Field>
              </Grid3>
            </>
          )}

          {step === 2 && !is1099 && (
            <>
              <h2 className="text-lg font-bold">Tax withholding (W-4)</h2>
              <p className="text-sm text-stone-600">Pick your federal filing status. This is what you marked on your W-4 form.</p>
              <Field label="Federal Filing Status *">
                <div className="grid grid-cols-1 gap-2 mt-1">
                  {["Single", "Married Filing Jointly", "Head of Household"].map(opt => (
                    <button key={opt} type="button" onClick={() => update("federal_filing_status", opt)} className={`p-3 rounded-lg border-2 text-sm font-semibold transition ${
                      form.federal_filing_status === opt ? "border-[#A61B1B] bg-rose-50 text-[#A61B1B]" : "border-stone-200 text-stone-700"
                    }`}>{opt}</button>
                  ))}
                </div>
              </Field>
              <Grid2>
                <Field label="Federal Allowances"><Input type="number" min={0} value={form.federal_allowances} onChange={e => update("federal_allowances", parseInt(e.target.value || 0))} /></Field>
                <Field label="Extra Withholding ($)"><Input type="number" min={0} step="0.01" value={form.federal_extra_withholding} onChange={e => update("federal_extra_withholding", parseFloat(e.target.value || 0))} /></Field>
              </Grid2>
              <Grid2>
                <Field label="IL Allowances"><Input type="number" min={0} value={form.il_allowances} onChange={e => update("il_allowances", parseInt(e.target.value || 0))} /></Field>
                <Field label="Extra IL Withholding ($)"><Input type="number" min={0} step="0.01" value={form.il_extra_withholding} onChange={e => update("il_extra_withholding", parseFloat(e.target.value || 0))} /></Field>
              </Grid2>
            </>
          )}

          {step === totalSteps && (
            <>
              <h2 className="text-lg font-bold">Direct deposit info</h2>
              <p className="text-sm text-stone-600">Grab a check — the routing number is the first 9 digits at the bottom-left. The account number comes after.</p>
              <Field label="Bank Name"><Input value={form.bank_name} onChange={e => update("bank_name", e.target.value)} placeholder="Chase, BMO Harris…" /></Field>
              <Field label="Account Type">
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {["checking", "savings"].map(t => (
                    <button key={t} type="button" onClick={() => update("bank_account_type", t)} className={`p-3 rounded-lg border-2 text-sm font-semibold capitalize ${
                      form.bank_account_type === t ? "border-emerald-700 bg-emerald-50 text-emerald-800" : "border-stone-200 text-stone-700"
                    }`}>{t}</button>
                  ))}
                </div>
              </Field>
              <Grid2>
                <Field label="Routing Number *">
                  <Input value={form.bank_routing} onChange={e => update("bank_routing", formatDigits(e.target.value, 9))} placeholder="9 digits" autoComplete="off" data-testid="emp-routing" />
                </Field>
                <Field label="Account Number *">
                  <Input value={form.bank_account} onChange={e => update("bank_account", formatDigits(e.target.value, 17))} autoComplete="off" data-testid="emp-account" />
                </Field>
              </Grid2>
            </>
          )}
        </div>

        {/* Nav */}
        <div className="bg-white rounded-lg shadow-sm p-4 flex items-center justify-between sticky bottom-0 z-10">
          <Button variant="outline" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} className="h-12" data-testid="emp-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <span className="text-xs text-stone-500 flex items-center gap-1 hidden sm:flex"><Lock className="w-3.5 h-3.5" /> Encrypted</span>
          {step < totalSteps ? (
            <Button onClick={() => setStep(step + 1)} className="h-12 px-6 bg-[#A61B1B] hover:bg-[#841515]" data-testid="emp-next">
              Continue <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={submitting} className="h-12 px-8 bg-emerald-700 hover:bg-emerald-800" data-testid="emp-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Submit Securely
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

const Grid2 = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
const Grid3 = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">{children}</div>;
const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs text-stone-600 mb-1 block">{label}</Label>
    {children}
  </div>
);
